/**
 * Helpers de path do bucket de uso por tenant
 * (`tenants/{tenantId}/billing/usage/days/{yyyymmdd}`).
 *
 * ## Por que isto existe
 *
 * O collection group `days` **não é exclusivo** desse bucket. Ele casa também
 * `aggregations/{daily,funnel,health,posthog,operation,inactiveTenants}/days`,
 * e esses seis docs carregam o **mesmo campo `yyyymmdd`** — ou seja, entram
 * numa `where('yyyymmdd', '==', X)` sem pedir licença.
 *
 * (`aggregations/dailyOpsHealth/days` também está no collection group, mas
 * grava o campo como `runYyyymmdd` — `monitorDailyOpsHealth.ts` —, então
 * escapa do `where` por acidente, não por desenho. É uma das razões de o
 * filtro aqui ser por PATH e não por campo: um rename lá não nos alcança.)
 *
 * A **soma** de `labelsPrinted` não corrompe (agregado global não tem esse
 * campo, contribui 0). O que corrompe é tudo que depende de *quantos docs
 * vieram* ou *de onde eles vieram*:
 *
 * - `snap.empty` / `snap.size` — usados como sentinela de "não há dado";
 * - `slice(0, limit)` — o CG sem `orderBy` ordena por `__name__`, e
 *   `aggregations/…` vem **antes** de `tenants/…`, então os agregados globais
 *   ocupam as primeiras posições da página;
 * - o `tenantId` extraído do path — vira string vazia, e
 *   `db.collection('tenants').doc('')` **lança** no Admin SDK.
 *
 * Toda leitura do CG `days` passa por aqui **antes** de contar, fatiar ou
 * derivar `tenantId`. Ver `docs/label-counters.md`.
 */

const USAGE_DAY_PATH = /^tenants\/([^/]+)\/billing\/usage\/days\/[^/]+$/;

/**
 * Extrai o `tenantId` de um path de bucket diário de uso, ou `null` se o path
 * não for um bucket de uso (agregado global, `daily_ops`, `months`, etc.).
 */
export function tenantIdFromUsageDayPath(path: string): string | null {
  return USAGE_DAY_PATH.exec(path)?.[1] ?? null;
}

/**
 * Snapshot mínimo que serve tanto ao Admin SDK (`QueryDocumentSnapshot`)
 * quanto ao SDK web (`QueryDocumentSnapshot` do `firebase/firestore`): os dois
 * expõem `ref.path`.
 */
type PathBearing = { readonly ref: { readonly path: string } };

/**
 * Filtra os docs de um `collectionGroup('days')` deixando só os buckets de uso
 * por tenant, já com o `tenantId` resolvido. Preserva a ordem de entrada.
 */
export function filterUsageDayDocs<T extends PathBearing>(
  docs: readonly T[],
): { tenantId: string; doc: T }[] {
  const out: { tenantId: string; doc: T }[] = [];
  for (const doc of docs) {
    const tenantId = tenantIdFromUsageDayPath(doc.ref.path);
    if (tenantId !== null) out.push({ tenantId, doc });
  }
  return out;
}
